import {helpdeskAxios} from '@/plugins/helpdeskAxios';
import {settings} from '@/plugins/settings';
import {IWhatsappMedia} from '@/model/Helpdesk/IWhatsapp';

// Files of WhatsApp messages are only fetched when the agent asks for them, and go straight to
// their PC: the helpdesk streams them from Meta (nothing kept on the server) and here nothing is
// cached or previewed, so every click is a new download.

const EXTENSIONS: {[mime: string]: string} = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'audio/ogg': 'ogg',
  'audio/mpeg': 'mp3',
  'audio/mp4': 'm4a',
  'audio/aac': 'aac',
  'audio/amr': 'amr',
  'video/mp4': 'mp4',
  'video/3gpp': '3gp',
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/plain': 'txt',
};

export function mediaFileName(media: IWhatsappMedia, fallbackName: string): string {
  if (media.filename) return media.filename;
  // "audio/ogg; codecs=opus" → "audio/ogg"
  const ext = EXTENSIONS[(media.mime || '').split(';')[0].trim()];
  return ext ? fallbackName + '.' + ext : fallbackName;
}

// The API answers errors as JSON, but the request asked for a blob: read the message out of it.
function errorMessage(error: any): Promise<string | null> {
  const data = error && error.response && error.response.data;
  if (!(data instanceof Blob)) return Promise.resolve(data && data.message || null);
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        resolve(JSON.parse(reader.result as string).message || null);
      } catch (e) {
        resolve(null);
      }
    };
    reader.onerror = () => resolve(null);
    reader.readAsText(data);
  });
}

// Rejects with {status, message}: status 404 means WhatsApp doesn't keep the file any more.
export function saveMedia(media: IWhatsappMedia, fallbackName: string): Promise<void> {
  if (!media.url) return Promise.reject({status: 0, message: null});
  return helpdeskAxios({url: settings.helpdeskEndpoint + media.url, method: 'GET', responseType: 'blob'})
    .then((res: any) => {
      // octet-stream so the browser never opens it, only saves it
      const url = URL.createObjectURL(new Blob([res.data], {type: 'application/octet-stream'}));
      const link = document.createElement('a');
      link.href = url;
      link.download = mediaFileName(media, fallbackName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    })
    .catch((error: any) => errorMessage(error).then((message) => {
      throw {status: error && error.response ? error.response.status : 0, message};
    }));
}
